import type React from "react";
import { useCallback, useMemo, useState, type KeyboardEvent } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Announcements,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Eye, EyeOff, GripVertical, RotateCcw, TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui";
import { formatMetricValue, computePctChange } from "../../lib/format";
import { priorComparisonName } from "../../lib/explore-date-presets";
import { moveCardByDelta } from "../../lib/card-prefs";
import { type MyBrandCardView } from "../../lib/metrics";
import { useTenantCurrency } from "@/lib/use-tenant-currency";

// ─── Types ────────────────────────────────────────────────────────────────────

export type MyBrandKpiCardsState = "loading" | "empty" | "ready" | "error";

interface MyBrandKpiCardsProps {
  state: MyBrandKpiCardsState;
  cards?: MyBrandCardView[];
  activePresetId: string;
  errorMessage?: string;
  selectedCards: Set<number>;
  onCardToggle: (index: number) => void;
  disabledCards?: ReadonlySet<number>;
  onCardDisabledToggle?: (index: number) => void;
  onCardOrderChange?: (order: number[]) => void;
  isOrderCustomized?: boolean;
  onCardOrderReset?: () => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_CLASS =
  "flex h-32 items-center justify-center rounded-xl border bg-card px-4 text-sm text-muted-foreground";

// ─── Sortable card ───────────────────────────────────────────────────────────

interface SortableKpiCardProps {
  card: MyBrandCardView;
  previousLabel: string;
  currencySymbol: string;
  isSelected: boolean;
  isDisabled: boolean;
  canReorder: boolean;
  onCardToggle: (index: number) => void;
  onCardDisabledToggle?: (index: number) => void;
  hideLocked: boolean;
  onMove: (index: number, delta: -1 | 1) => void;
  t: ReturnType<typeof useTranslation>["t"];
}

function SortableKpiCard({
  card,
  previousLabel,
  currencySymbol,
  isSelected,
  isDisabled,
  canReorder,
  onCardToggle,
  onCardDisabledToggle,
  hideLocked,
  onMove,
  t,
}: SortableKpiCardProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({
      id: card.index,
      disabled: !canReorder,
    });

  const pctChange = computePctChange(card.value, card.previousValue);
  const isPositive = pctChange >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  const outlineColor = isDisabled || !isSelected ? "var(--border)" : `var(${card.colorToken})`;

  const eyeLabel = isDisabled
    ? t("dashboard.kpiCards.showCard", "Show {{label}}", { label: card.label })
    : hideLocked
      ? t("dashboard.kpiCards.lastCardLocked", "At least one KPI card must stay visible")
      : t("dashboard.kpiCards.hideCard", "Hide {{label}}", { label: card.label });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        "--card-outline": outlineColor,
      } as React.CSSProperties}
      className={cn(
        "relative rounded-xl border bg-card outline outline-2 outline-offset-2 [outline-color:var(--card-outline)]",
        "transition-[outline-color]",
        isDisabled && "bg-muted/30",
        isDragging && "z-10 opacity-90 shadow-lg",
      )}
    >
      <button
        type="button"
        aria-pressed={isSelected}
        disabled={isDisabled}
        onClick={() => onCardToggle(card.index)}
        className={cn(
          "flex w-full flex-col gap-2 rounded-xl p-4 pr-[4.5rem] text-left cursor-pointer select-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          isDisabled && "cursor-default opacity-50 grayscale",
        )}
      >
        <div className={cn("flex flex-col gap-2", isDisabled && "blur-sm")}>
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              "h-2.5 w-2.5 shrink-0 rounded-full transition-[background,box-shadow]",
              isDisabled && "bg-transparent ring-1 ring-muted-foreground/40",
            )}
            style={
              isDisabled
                ? undefined
                : isSelected
                  ? { background: `var(${card.colorToken})` }
                  : {
                      background: "transparent",
                      boxShadow: `0 0 0 1.5px var(${card.colorToken})`,
                    }
            }
          />
          <span className={cn("text-xs font-medium", isDisabled ? "text-muted-foreground" : "text-muted-foreground")}>
            {card.label}
          </span>
        </div>

        <span
          className={cn(
            "text-xl font-semibold tracking-tight",
            isDisabled ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {formatMetricValue(card.value, card.format, currencySymbol)}
        </span>

        <div
          className={cn(
            "flex items-center gap-1 text-xs font-medium",
            isDisabled
              ? "text-muted-foreground"
              : isPositive
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400",
          )}
        >
          <TrendIcon className="h-3 w-3 shrink-0" />
          <span>
            {t("dashboard.kpiCards.vsPriorWindow", "{{sign}}{{pct}}% vs. prior {{previous}}", {
              sign: isPositive ? "+" : "",
              pct: pctChange.toFixed(2),
              previous: previousLabel,
            })}
          </span>
        </div>
        </div>
      </button>

      <div className="absolute right-2 top-2 flex items-center gap-0.5">
        {canReorder && (
          <button
            type="button"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            aria-label={t("dashboard.kpiCards.reorderCard", "Reorder {{label}}", { label: card.label })}
            className="flex h-7 w-7 touch-none cursor-grab items-center justify-center rounded-md text-muted-foreground/60 hover:text-foreground active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:h-9 [@media(pointer:coarse)]:w-9"
            onPointerDown={(e) => {
              e.stopPropagation();
              listeners?.onPointerDown?.(e);
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
              // Mid-drag arrows belong to dnd-kit's KeyboardSensor; stopping them here would starve it.
              if (isDragging || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) {
                listeners?.onKeyDown?.(e);
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              onMove(card.index, e.key === "ArrowLeft" ? -1 : 1);
            }}
          >
            <GripVertical className="h-4 w-4" aria-hidden />
          </button>
        )}
        {onCardDisabledToggle && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                disabled={hideLocked && !isDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hideLocked && !isDisabled) return;
                  onCardDisabledToggle(card.index);
                }}
                aria-label={eyeLabel}
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 [@media(pointer:coarse)]:h-9 [@media(pointer:coarse)]:w-9"
              >
                {isDisabled ? (
                  <EyeOff className="h-4 w-4" aria-hidden />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{eyeLabel}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function MyBrandKpiCards({
  state,
  cards,
  activePresetId,
  errorMessage,
  selectedCards,
  onCardToggle,
  disabledCards,
  onCardDisabledToggle,
  onCardOrderChange,
  isOrderCustomized,
  onCardOrderReset,
}: MyBrandKpiCardsProps) {
  const { t } = useTranslation();
  const { symbol: currencySymbol } = useTenantCurrency();
  const [reorderStatus, setReorderStatus] = useState("");

  const canReorder = Boolean(onCardOrderChange && cards && cards.length > 0);
  const ids = useMemo(() => cards?.map((c) => c.index) ?? [], [cards]);
  const enabledVisibleCount = (cards?.length ?? 0) - (disabledCards?.size ?? 0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const labelForIndex = useCallback(
    (index: number) => cards?.find((c) => c.index === index)?.label ?? String(index),
    [cards],
  );

  const positionOf = useCallback(
    (index: number) => {
      const pos = ids.indexOf(index);
      return pos >= 0 ? pos + 1 : 0;
    },
    [ids],
  );

  const handleMove = useCallback(
    (index: number, delta: -1 | 1) => {
      if (!onCardOrderChange) return;
      const next = moveCardByDelta(ids, index, delta);
      if (next === ids) return;
      onCardOrderChange([...next]);
      const newPos = next.indexOf(index) + 1;
      setReorderStatus(
        t("dashboard.kpiCards.reorderMoved", "{{label}} moved to position {{position}} of {{total}}", {
          label: labelForIndex(index),
          position: newPos,
          total: next.length,
        }),
      );
    },
    [ids, labelForIndex, onCardOrderChange, t],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onCardOrderChange) return;
      const from = ids.indexOf(Number(active.id));
      const to = ids.indexOf(Number(over.id));
      if (from < 0 || to < 0) return;
      const next = arrayMove(ids, from, to);
      onCardOrderChange(next);
      const index = Number(active.id);
      setReorderStatus(
        t("dashboard.kpiCards.reorderMoved", "{{label}} moved to position {{position}} of {{total}}", {
          label: labelForIndex(index),
          position: next.indexOf(index) + 1,
          total: next.length,
        }),
      );
    },
    [ids, labelForIndex, onCardOrderChange, t],
  );

  const accessibilityAnnouncements = useMemo((): Announcements => {
    const total = ids.length;
    return {
      onDragStart({ active }) {
        const index = Number(active.id);
        return t("dashboard.kpiCards.reorderPickedUp", "Picked up {{label}}, position {{position}} of {{total}}", {
          label: labelForIndex(index),
          position: positionOf(index),
          total,
        });
      },
      onDragOver({ active, over }) {
        if (!over) return;
        const index = Number(active.id);
        const overIndex = Number(over.id);
        const position = ids.indexOf(overIndex) + 1;
        return t("dashboard.kpiCards.reorderMoved", "{{label}} moved to position {{position}} of {{total}}", {
          label: labelForIndex(index),
          position,
          total,
        });
      },
      onDragEnd({ active, over }) {
        if (!over) return;
        const index = Number(active.id);
        const position = ids.indexOf(Number(over.id)) + 1;
        return t("dashboard.kpiCards.reorderDropped", "{{label}} dropped at position {{position}} of {{total}}", {
          label: labelForIndex(index),
          position,
          total,
        });
      },
      onDragCancel({ active }) {
        const index = Number(active.id);
        return t(
          "dashboard.kpiCards.reorderCancelled",
          "Reorder cancelled, {{label}} returned to position {{position}} of {{total}}",
          {
            label: labelForIndex(index),
            position: positionOf(index),
            total,
          },
        );
      },
    };
  }, [ids, labelForIndex, positionOf, t]);

  if (state === "loading") {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="flex flex-col gap-3 rounded-xl border bg-card p-4"
            style={{ "--shimmer-delay": `${i * 160}ms` } as React.CSSProperties}
          >
            <div className="skeleton-shimmer-bar h-3 w-20 rounded-md" />
            <div className="skeleton-shimmer-bar h-7 w-24 rounded-md" />
            <div className="skeleton-shimmer-bar h-3 w-16 rounded-md" />
          </div>
        ))}
      </div>
    );
  }

  if (state === "empty") {
    return (
      <div className={PLACEHOLDER_CLASS}>
        No data available for this period.
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className={PLACEHOLDER_CLASS}>
        {errorMessage ?? "Your data is being analyzed — something interesting is coming!"}
      </div>
    );
  }

  if (!cards) return null;

  const previousLabel = priorComparisonName(activePresetId, t);

  const grid = (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((card) => (
        <SortableKpiCard
          key={card.index}
          card={card}
          previousLabel={previousLabel}
          currencySymbol={currencySymbol}
          isSelected={selectedCards.has(card.index)}
          isDisabled={disabledCards?.has(card.index) ?? false}
          canReorder={canReorder}
          onCardToggle={onCardToggle}
          onCardDisabledToggle={onCardDisabledToggle}
          hideLocked={
            !!onCardDisabledToggle &&
            !disabledCards?.has(card.index) &&
            enabledVisibleCount <= 1
          }
          onMove={handleMove}
          t={t}
        />
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-2">
      {isOrderCustomized && onCardOrderReset && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCardOrderReset}
            className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            {t("dashboard.kpiCards.resetLayout", "Reset card layout")}
          </button>
        </div>
      )}
      {reorderStatus ? (
        <div role="status" aria-live="polite" className="sr-only">
          {reorderStatus}
        </div>
      ) : null}
      {canReorder ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          accessibility={{
            announcements: accessibilityAnnouncements,
            screenReaderInstructions: {
              draggable: t(
                "dashboard.kpiCards.reorderInstructions",
                "To reorder, focus a card's drag handle, press Space, move with the arrow keys, then press Space to drop. Or press Left/Right Arrow on the handle to move one position.",
              ),
            },
          }}
        >
          <SortableContext items={ids} strategy={rectSortingStrategy}>
            {grid}
          </SortableContext>
        </DndContext>
      ) : (
        grid
      )}
    </div>
  );
}
